import { parseDistributorData, groupDistributorsByLocation, getDistributorType } from '../utils/distributorData';

// In-memory cache for distributors data
let distributorsCache = null;
let groupedDistributorsCache = null;

// Fallback distributor for Estado de México
const edomexFallbackDistributor = {
  Name: "ECOSOFT (Distribuidores Estado de México)",
  Address: "Contactar para información sobre distribuidores en Estado de México",
  Phone: "(55) 3990.8312",
  Email: "ventas@ecosoft.com.mx",
  Website: "https://ecosoft.com.mx/",
  Location: "Estado de México"
};

/**
 * Shuffles an array using Fisher-Yates algorithm
 * @param {Array} array - The array to shuffle
 * @returns {Array} The shuffled array
 */
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

/**
 * Load distributors from the CSV files
 * @returns {Promise<Array>} Array of distributor objects
 */
export const loadDistributors = async () => {
  if (distributorsCache) {
    return distributorsCache;
  }

  try {
    // Load main distributors file with UTF-8 encoding
    const response = await fetch('/da.csv');
    const blob = await response.blob();
    const reader = new FileReader();
    
    const mainDistributors = await new Promise((resolve, reject) => {
      reader.onload = () => {
        try {
          const csvData = reader.result;
          const parsed = parseDistributorData(csvData);
          resolve(parsed);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsText(blob, 'UTF-8'); // Use UTF-8 encoding for Spanish characters
    });
    
    // Load Estado de México distributors file with UTF-8 encoding
    try {
      const edomexResponse = await fetch('/daEDO.csv');
      const edomexBlob = await edomexResponse.blob();
      const edomexReader = new FileReader();
      
      const edomexDistributors = await new Promise((resolve, reject) => {
        edomexReader.onload = () => {
          try {
            const edomexCsvData = edomexReader.result;
            const parsed = parseDistributorData(edomexCsvData);
            resolve(parsed);
          } catch (error) {
            reject(error);
          }
        };
        edomexReader.onerror = () => reject(edomexReader.error);
        edomexReader.readAsText(edomexBlob, 'UTF-8'); // Use UTF-8 encoding
      });
      
      console.log(`Loaded ${edomexDistributors.length} Estado de México distributors from daEDO.csv`);
      
      // Combine both data sets
      distributorsCache = [...mainDistributors, ...edomexDistributors];
    } catch (edomexError) {
      console.warn('Could not load Estado de México distributors file, using main file only:', edomexError);
      distributorsCache = mainDistributors;
    }
    
    return distributorsCache;
  } catch (error) {
    console.error('Error loading distributors data:', error);
    return [];
  }
};

/**
 * Get all distributors
 * @returns {Promise<Array>} Array of distributor objects
 */
export const getAllDistributors = async () => {
  // Forzar recarga de datos para evitar problemas de caché
  distributorsCache = null;
  return await loadDistributors();
};

/**
 * Get distributors grouped by location
 * @returns {Promise<Object>} Object with locations as keys and arrays of distributors as values
 */
export const getDistributorsByLocation = async () => {
  if (groupedDistributorsCache) {
    return groupedDistributorsCache;
  }

  const distributors = await loadDistributors();
  groupedDistributorsCache = groupDistributorsByLocation(distributors);
  return groupedDistributorsCache;
};

/**
 * Get distributors by country or state
 * @param {string} location - The name of the location to filter by
 * @returns {Promise<Array>} Array of distributors in the specified location
 */
export const getDistributorsByLocationName = async (location) => {
  // Cargar todos los distribuidores directamente desde el CSV
  const allDistributors = await loadDistributors();
  
  console.log(`Buscando distribuidores para: ${location}`);
  console.log(`Total distribuidores cargados: ${allDistributors.length}`);
  
  // Caso especial para 'edomex'
  if (location.toLowerCase() === 'edomex') {
    console.log('Buscando distribuidores específicamente con Location="edomex"');
    const edomexDirect = allDistributors.filter(d => 
      d.Location && d.Location.toLowerCase() === 'edomex'
    );
    
    if (edomexDirect.length > 0) {
      console.log(`Encontrados ${edomexDirect.length} distribuidores con Location="edomex"`);
      return shuffleArray(edomexDirect);
    }
    
    // Si no encontramos nada, redirigir a Estado de México
    location = 'Estado de México';
    console.log('No se encontraron resultados para edomex, redirigiendo a Estado de México');
  }
  
  // Normalizar el nombre de la ubicación para búsqueda
  const normalizedSearchLocation = location.toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  // Función para comprobar si un distribuidor pertenece a Ciudad de México (CDMX)
  const isCDMXDistributor = (d) => {
    if (!d.Location) return false;
    
    const locationLower = d.Location.toLowerCase();
    const addressLower = (d.Address || '').toLowerCase();
    
    // Verificar por términos específicos de CDMX
    return locationLower === 'cdmx' || 
           locationLower === 'ciudad de méxico' || 
           locationLower === 'ciudad de mexico' ||
           locationLower === 'df' || 
           locationLower === 'd.f.' || 
           locationLower === 'distrito federal' ||
           addressLower.includes('cdmx') || 
           addressLower.includes('ciudad de méxico') || 
           addressLower.includes('ciudad de mexico') ||
           addressLower.includes('distrito federal') ||
           // Importante: mexico sin "estado de" normalmente refiere a Ciudad de México
           (addressLower.includes('mexico') && 
            !addressLower.includes('estado de mexico') && 
            !addressLower.includes('estado de méxico'));
  };
  
  // Función para comprobar si un distribuidor pertenece a Estado de México
  const isEdoMexDistributor = (d) => {
    // Temporarily log for specific debugging of EdoMex entries
    if ( (d.Location && d.Location.toLowerCase().includes('estado de m')) || 
         (d.Location && d.Location.toLowerCase() === 'edomex') || 
         (d.Address && d.Address.toLowerCase().includes('estado de m')) || 
         (d.Address && d.Address.toLowerCase().includes('edomex')) ) {
      console.log(`[isEdoMexDistributor DEBUG] Checking entry: Name: ${d.Name}, Raw Location: '${d.Location}', Raw Address: '${d.Address}'`);
    }

    const locationLower = (d.Location || '').toLowerCase().trim(); // Added trim here for safety
    const addressLower = (d.Address || '').toLowerCase().trim(); // Added trim here for safety

    // Check Location field first
    if (locationLower === 'edomex' ||
        locationLower === 'edo. mex.' ||
        locationLower === 'edo mex' ||
        locationLower === 'edo. de mex.' ||
        locationLower === 'estado de méxico' ||
        locationLower === 'estado de mexico') {
      console.log(`[isEdoMexDistributor DEBUG] Matched by Location: '${locationLower}'`);
      return true;
    }

    // If Location doesn't match, check Address field
    if (addressLower.includes('edomex') ||
        addressLower.includes('edo. mex') ||
        addressLower.includes('edo mex') ||
        addressLower.includes('estado de méxico') || 
        addressLower.includes('estado de mexico') ||
        (addressLower.includes('edo') && addressLower.includes('mex'))) {
      console.log(`[isEdoMexDistributor DEBUG] Matched by Address: criteria found in '${addressLower}'`);
      return true;
    }
    return false;
  };

  // Función para comprobar si un distribuidor pertenece a Yucatán
  const isYucatanDistributor = (d) => {
    if (!d.Location) return false;
    
    const locationLower = d.Location.toLowerCase();
    const addressLower = (d.Address || '').toLowerCase();
    
    return locationLower.includes('yucatán') || 
           locationLower.includes('yucatan') || 
           locationLower.includes('mérida') || 
           locationLower.includes('merida') ||
           addressLower.includes('yucatán') || 
           addressLower.includes('yucatan') || 
           addressLower.includes('mérida') || 
           addressLower.includes('merida');
  };

  // Función para comprobar si un distribuidor pertenece a Nuevo León
  const isNuevoLeonDistributor = (d) => {
    if (!d.Location) return false;
    
    const locationLower = d.Location.toLowerCase();
    const addressLower = (d.Address || '').toLowerCase();
    
    return locationLower.includes('nuevo león') || 
           locationLower.includes('nuevo leon') || 
           locationLower.includes('monterrey') ||
           addressLower.includes('nuevo león') || 
           addressLower.includes('nuevo leon') || 
           addressLower.includes('monterrey');
  };

  // Casos específicos para los estados problemáticos
  if (location === 'Ciudad de México' || location === 'CDMX') {
    const cdmxDistributors = allDistributors.filter(isCDMXDistributor);
    console.log(`Encontrados ${cdmxDistributors.length} distribuidores para CDMX`);
    return cdmxDistributors.length > 0 ? shuffleArray(cdmxDistributors) : [];
  }
  
  if (location === 'Estado de México') {
    // Usar directamente el filtro isEdoMexDistributor, 
    // ya que este considera 'edomex' en d.Location y otros términos.
    const edomexDistributors = allDistributors.filter(isEdoMexDistributor);
    console.log(`Encontrados ${edomexDistributors.length} distribuidores para Estado de México (usando isEdoMexDistributor)`);
    // Log the actual data found for debugging
    if (edomexDistributors.length > 0) {
      console.log('Datos de distribuidores para EdoMex (después de isEdoMexDistributor):', JSON.stringify(edomexDistributors.map(d => ({ Name: d.Name, Address: d.Address, Location: d.Location })), null, 2));
      return shuffleArray(edomexDistributors);
    } else {
      // Si no se encontraron distribuidores, usar el distribuidor de fallback para Estado de México
      console.log('Usando distribuidor de fallback para Estado de México');
      return [edomexFallbackDistributor];
    }
  }
  
  if (location === 'Yucatán') {
    const yucatanDistributors = allDistributors.filter(isYucatanDistributor);
    console.log(`Encontrados ${yucatanDistributors.length} distribuidores para Yucatán`);
    return yucatanDistributors.length > 0 ? shuffleArray(yucatanDistributors) : [];
  }
  
  if (location === 'Nuevo León') {
    const nlDistributors = allDistributors.filter(isNuevoLeonDistributor);
    console.log(`Encontrados ${nlDistributors.length} distribuidores para Nuevo León`);
    return nlDistributors.length > 0 ? shuffleArray(nlDistributors) : [];
  }
  
  // Para otros estados, buscar coincidencia exacta primero
  const exactMatches = allDistributors.filter(d => {
    if (!d.Location) return false;
    return d.Location.toLowerCase() === location.toLowerCase();
  });
  
  if (exactMatches.length > 0) {
    console.log(`Encontrados ${exactMatches.length} distribuidores exactos para ${location}`);
    return shuffleArray(exactMatches);
  }
  
  // Luego buscar por coincidencia parcial
  const partialMatches = allDistributors.filter(d => {
    if (!d.Location) return false;
    // Asegurarse de que no mezclamos CDMX o EdoMex con otros estados
    if (isCDMXDistributor(d) || isEdoMexDistributor(d)) {
      return false;
    }
    return d.Location.toLowerCase().includes(location.toLowerCase());
  });
  
  if (partialMatches.length > 0) {
    console.log(`Encontrados ${partialMatches.length} distribuidores parciales para ${location}`);
    return shuffleArray(partialMatches);
  }
  
  // Finalmente búsqueda normalizada (sin acentos)
  const normalizedMatches = allDistributors.filter(d => {
    if (!d.Location) return false;
    // Asegurarse de que no mezclamos CDMX o EdoMex con otros estados
    if (isCDMXDistributor(d) || isEdoMexDistributor(d)) {
      return false;
    }
    
    const normalizedDist = d.Location.toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    
    return normalizedDist.includes(normalizedSearchLocation);
  });
  
  if (normalizedMatches.length > 0) {
    console.log(`Encontrados ${normalizedMatches.length} distribuidores normalizados para ${location}`);
    return shuffleArray(normalizedMatches);
  }
  
  // No se encontraron distribuidores
  console.log(`No se encontraron distribuidores para ${location}`);
  return [];
};

/**
 * Get all unique locations where distributors exist
 * @returns {Promise<Array>} Array of location objects
 */
export const getDistributorLocations = async () => {
  const grouped = await getDistributorsByLocation();
  
  return Object.keys(grouped).map(name => {
    // Check if the location is international or mexican
    const isMexican = grouped[name].some(dist => 
      getDistributorType(dist) === 'mexico'
    );
    
    return {
      name,
      type: isMexican ? 'mexico' : 'international'
    };
  });
};

/**
 * Get locations based on type (mexican or international)
 * @param {string} type - Type of location ('mexico', 'international', or 'all')
 * @returns {Promise<Array>} Filtered array of location objects
 */
export const getLocationsByType = async (type) => {
  const locations = await getDistributorLocations();
  
  if (type === 'all') {
    return locations;
  }
  
  return locations.filter(location => location.type === type);
}; 